import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../../../../src/lib/supabase';
import { normalizeMesReferencia } from '../../../../../src/lib/mesReferencia';
import { derivarStatusBoleto } from '../../../../../src/lib/statusBoleto';
import type { FaturaPainel, FaturaPainelRaw } from '../../../../../src/lib/financeiro/types';

const COLUNAS =
  'id,cliente_nome,cliente_cpf_cnpj,uc_codigo,mes_referencia,valor_boleto_vigor,valor_pagamento,' +
  'status_boleto_vigor,status_fatura,vencimento_boleto,data_vencimento,data_pgto_boleto,quitado_em,' +
  'cancelado_em,parcelamento_descricao,unidade_geradora_1,empresa_parceira_id,' +
  'energia_injetada_gd1_kwh,energia_injetada_gd2_kwh,' +
  // Sem `valor_emissao_boleto_unico` o boleto único da Gran entrava com o valor
  // errado nos cards (`valorDevido` lê daqui) — o Painel Financeiro já buscava.
  'valor_emissao_boleto_unico,url_boleto,url_boleto_combinado,pdf_boleto_combinado,' +
  // Coluna "Parte da Vigor" da lista de boletos — ver `parteVigorDaFatura`.
  'valor_fatura_distribuidora,equatorial_quitada_pelo_cliente,debito_automatico_equatorial,qtd_injecao_scee_boleto,' +
  // Filtro "Data por: mês de emissão" — ver `dataDaBase`.
  'boleto_emissao_data,emitido_em,' +
  // Fatura de UC de agrupamento não vence pela própria data (#6284) — ver `derivarStatusBoleto`.
  'cobranca_agrupada,' +
  // Aba CEO: a injeção da usina no ciclo, impressa na conta do cliente, e o
  // consumo da conta da própria usina — ver `financeiroPorUsina`.
  'geracao_unidade_geradora_1,consumo_ativo_kwh';

const INTERVALO_AUTO_REFRESH_MS = 10 * 60 * 1000; // 10 min, mesmo intervalo do Painel Financeiro Executivo

const cacheValido = (): boolean => !!cache && Date.now() - cache.quando < INTERVALO_AUTO_REFRESH_MS;

export async function buscarTodasAsFaturas(): Promise<FaturaPainelRaw[]> {
  let todas: FaturaPainelRaw[] = [];
  let inicio = 0;
  const tamanhoPagina = 1000;
  while (true) {
    const { data, error } = await supabase
      .from('faturas')
      .select(COLUNAS)
      .eq('ambiente', 'producao')
      // Cliente próprio da parceira (boleto avulso da Gran): a Vigor não recebe nada dele.
      .eq('fora_parceria_vigor', false)
      .neq('status_fatura', 'Excluído')
      // Sem ordem fixa o Postgres não garante a mesma sequência entre uma
      // página e outra: `.range` sozinho pode pular ou repetir faturas, e o
      // total do card muda de uma carga para outra.
      .order('id', { ascending: true })
      .range(inicio, inicio + tamanhoPagina - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    todas = todas.concat(data as unknown as FaturaPainelRaw[]);
    if (data.length < tamanhoPagina) break;
    inicio += tamanhoPagina;
  }
  return todas;
}

/** `empresas_parceiras.percentual_vigor` por id — a fatia da Vigor no boleto da parceira. */
export async function buscarPercentuaisVigor(): Promise<Map<string, number>> {
  const { data, error } = await supabase.from('empresas_parceiras').select('id, percentual_vigor');
  if (error) throw error;
  return new Map((data || []).map((p: any) => [p.id as string, Number(p.percentual_vigor) || 0]));
}

/**
 * Última carga, compartilhada entre quem usa este hook.
 *
 * O painel tem DOIS consumidores ao mesmo tempo: o bloco Financeiro da Visão
 * geral e a aba Financeiro. Sem isto, sair da capa para a aba buscava as ~7 mil
 * faturas de novo, do zero, a cada troca. A validade é a mesma do auto-refresh,
 * então o dado nunca fica mais velho do que já ficaria.
 */
let cache: { dados: FaturaPainel[]; percentuais: Map<string, number>; quando: number } | null = null;

/**
 * Busca em andamento, compartilhada. A aba CEO e a Visão Geral da Diretoria montam
 * dois consumidores ao mesmo tempo (o bloco e o contexto dos KPIs): sem isto, com
 * o cache vazio, as ~8 mil faturas desciam duas vezes em paralelo.
 */
let emAndamento: Promise<void> | null = null;

async function buscarECachear(): Promise<void> {
  const [brutas, percentuais] = await Promise.all([buscarTodasAsFaturas(), buscarPercentuaisVigor()]);
  const agora = new Date();
  const comStatus: FaturaPainel[] = brutas.map((f) => ({
    ...f,
    mes_referencia: normalizeMesReferencia(f.mes_referencia),
    statusDerivado: derivarStatusBoleto(f, agora),
  }));
  cache = { dados: comStatus, percentuais, quando: Date.now() };
}

export function useFinanceiroDashboardData() {
  const [dados, setDados] = useState<FaturaPainel[]>(() => (cacheValido() ? cache!.dados : []));
  const [percentualVigorPorParceira, setPercentualVigorPorParceira] = useState<Map<string, number>>(
    () => (cacheValido() ? cache!.percentuais : new Map()),
  );
  const [loading, setLoading] = useState(!cacheValido());
  const [erro, setErro] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const carregar = useCallback(async (forcar = true) => {
    if (!forcar && cacheValido()) {
      setDados(cache!.dados);
      setPercentualVigorPorParceira(cache!.percentuais);
      setLoading(false);
      return;
    }
    setLoading(true);
    setErro(null);
    try {
      // Quem chega com outra busca no ar espera a mesma, em vez de baixar tudo de novo.
      if (!emAndamento) emAndamento = buscarECachear().finally(() => { emAndamento = null; });
      await emAndamento;
      setPercentualVigorPorParceira(cache!.percentuais);
      setDados(cache!.dados);
    } catch (e: any) {
      console.error('Erro ao carregar seção Financeiro do Painel Executivo:', e);
      setErro(e?.message || 'Erro ao carregar os dados financeiros.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Na montagem, aproveita o que já foi buscado há pouco; o timer continua
    // atualizando de verdade a cada 10 minutos.
    carregar(false);
    intervalRef.current = setInterval(() => carregar(true), INTERVALO_AUTO_REFRESH_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [carregar]);

  return { dados, percentualVigorPorParceira, loading, erro, refetch: carregar };
}
